"use client";
import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { FaArrowRightLong } from "react-icons/fa6";
import { FaEyeSlash } from "react-icons/fa";
import { IoEyeSharp } from "react-icons/io5";
import { useAuthContext } from "@/context/auth-context";
import { toast } from "sonner";
import { z } from "zod";
import useSession from "@/utils/use-session";
import handleRecaptchaValidation from "@/components/google-recaptcha/recaptcha-function";
import browserPersistence from "@/utils/session-persistance";
import { CUSTOMER_DETAILS_KEY } from "@/utils/form-data-manager";
import { useEventContext } from "@/context/events/event-context";
import { sessionStorageCleanup } from "@/utils/helper";
import LoaderWithMessage from "@/components/loading/loader-with-message";
import { useGlobalData } from "@/context/global-state-context";

const validatePassword = z.object({
  password: z.string()
    .min(1, { message: "This is a required field." })
    .max(64, { message: "Password cannot exceed 64 characters." }),
});

const validateEmail = z.object({
  email: z.string()
    .min(1, { message: "This is a required field." })
    .max(128, { message: "Email cannot exceed 128 characters." })
    .email({ message: "Enter a valid email address (Ex: johndoe@domain.com)." })
    .refine((val) => val === val.trim(), {
      message: "Email cannot have spaces at the beginning or end.",
    }),
});

const sessionStorage = new browserPersistence();

export default function LoginForm() {
  const { isLoggedIn } = useAuthContext();
  const { login, isLoading, getCustomerDetails, getCpqLogin } = useSession();
  const { handleRecaptcha } = handleRecaptchaValidation();
  const { message, setMessage, clearMessage } = useGlobalData();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [localLoading, setLocalLoading] = useState(false);

  const [emailIsValid, setEmailIsValid] = useState(true);
  const [passwordIsValid, setPasswordIsValid] = useState(true);
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const { setIsLoggedIn, setCustomerDetails, setCpqSessionStatus } =
    useAuthContext();

  const router = useRouter();
  const searchParams = useSearchParams();
  const { handleSFSignInEvent } = useEventContext();

  const source = searchParams?.get("source") ?? "";
  const quoteId = searchParams?.get("quoteId") ?? "";
  const path = searchParams?.get("path") ?? "";

  const [sfAuthState, setSfAuthState] = useState<"idle" | "loading" | "failed">("idle");
  const sfInitiated = useRef(false);

  useEffect(() => {
    if (source !== "salesforce" || sfInitiated.current) return;
    sfInitiated.current = true;

    const sfRetryKey = `sf_login_attempt_${quoteId}`;
    const lastAttempt = window.sessionStorage.getItem(sfRetryKey);
    // Dev block flag should only apply during re-auth (guard redirect), not fresh SF entry.
    // Fresh SF entry has no lastAttempt, so isReauthBlocked alone won't trigger the error.
    const isReauthBlocked =
      lastAttempt && window.sessionStorage.getItem("__dev_block_sf_reauth") === "true";
    const now = Date.now();

    // If we attempted SF sign-in within the last 30 seconds, it means
    // Salesforce OAuth failed and redirected us back — don't retry.
    // Also triggered immediately if re-auth is blocked (dev testing).
    const isFailedRetry = lastAttempt && now - Number(lastAttempt) < 30000;

    if (isFailedRetry || isReauthBlocked) {
      window.sessionStorage.removeItem(sfRetryKey);
      setSfAuthState("failed");
      return;
    }

    const pathMapping: Record<string, string> = {
      "global-options": `/configurator/global-options?quoteId=${quoteId}&source=${source}`,
      "body-options": `/configurator/body-options?quoteId=${quoteId}&source=${source}&redirect=body-options`,
      "quote-management": `/configurator/qm/edit?quoteId=${quoteId}&source=${source}&redirect=quote-management`,
    };
    const callbackUrl = pathMapping[path];
    if (callbackUrl) {
      setSfAuthState("loading");
      // Mark this attempt so we can detect a failed redirect loop
      window.sessionStorage.setItem(sfRetryKey, String(now));
      handleSFSignInEvent({ callbackUrl });
    }
  }, [source, quoteId, path, handleSFSignInEvent]);

  if (source === "salesforce") {
    if (sfAuthState === "failed") {
      return (
        <div className="flex flex-col items-center justify-center gap-4 p-8">
          <h2 className="text-primary-700 font-semibold text-xl font-myriad">
            Salesforce Login Failed
          </h2>
          <p className="text-neutral-500 text-center text-sm">
            We were unable to authenticate with Salesforce. Please go back to
            Salesforce and try opening the quote again.
          </p>
          <a
            href={process.env.NEXT_PUBLIC_REDIRECT_SALESFORCE_ENDPOINT}
            className="btn btn-primary px-6 py-2 rounded text-sm"
          >
            Return to Salesforce
          </a>
        </div>
      );
    }
    return <LoaderWithMessage message="Signing in with Salesforce..." />;
  }

  const togglePasswordVisibility = (e: any) => {
    e.preventDefault();
    setPasswordVisible(!passwordVisible);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setEmail(e.target.value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setPassword(e.target.value);
  };

  const formSubmissionHandler = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isEmailValid = validateEmail.safeParse({ email });
    const isPasswordValid = validatePassword.safeParse({ password });

    setPasswordIsValid(true);
    setEmailIsValid(true);

    if (!isEmailValid.success) {
      setEmailError(isEmailValid.error.errors[0].message);
      setEmailIsValid(false);
    }
    if (!isPasswordValid.success) {
      setPasswordError(isPasswordValid.error.errors[0].message);
      setPasswordIsValid(false);
    }
    if (!isEmailValid.success || !isPasswordValid.success) return;

    const recaptchaValid = await handleRecaptcha();

    if (recaptchaValid) {
      handleLogin(email, password);
    } else {
      toast.error("Recaptcha validation failed.");
    }
  };

  const handleLogin = async (email: string, password: string) => {
    setLocalLoading(true);
    setMessage("Logging you in ...");
    try {
      const response = await login({ email: email, password: password });

      if (!response.isLoggedIn) {
        toast.error(response.error);
        setLocalLoading(false);
        return;
      }
      const { token } = response;
      const customerResponse = await getCustomerDetails({ token: token });

      if (customerResponse.error) {
        toast.error(customerResponse.error);
        setLocalLoading(false);
        return;
      }

      sessionStorage.setItem(CUSTOMER_DETAILS_KEY, customerResponse);
      setCustomerDetails(customerResponse);
      setIsLoggedIn(true);

      const cpqResponse = await getCpqLogin();
      setCpqSessionStatus(!cpqResponse.error);
      sessionStorageCleanup("logoutCleanup");

      window.location.replace("/");
    } catch (error) {
      console.error("Login error:", error);
      toast.error("An unexpected error occurred. Please try again.");
      setLocalLoading(false);
      clearMessage();
    }
  };
  return (
    <>
      {localLoading && <LoaderWithMessage message={message} />}
      <form className="w-full" onSubmit={formSubmissionHandler} name="login">
        {/* Header  */}
        <div>
          <h1 className=" text-primary-700 font-semibold text-3xl text-center mb-2 leading-6 font-myriad">
            Customer Login
          </h1>
          <p className="text-center text-base font-normal leading-4 text-neutral-500 mt-2 font-400 font-calibri">
            If you have an account, sign in with your Email.
          </p>
        </div>

        {/* Email Input */}
        <div className="flex flex-col mt-9 w-full">
          <label htmlFor="email" className="input-label">
            Email Address <span className="text-danger-600">*</span>
          </label>
          <input
            data-cy="email"
            type="string"
            name="username"
            id="email"
            onChange={handleEmailChange}
            value={email}
            placeholder="Email Address"
            className={`input ${!emailIsValid && "input-error-border"}`}
            disabled={isLoading}
          />

          {!emailIsValid && (
            <p className="input-error-msg mt-1" data-cy="required-field-error">
              {emailError}
            </p>
          )}
        </div>

        {/* Password Input  */}
        <div className="flex flex-col mt-4">
          <label htmlFor="password" className="input-label">
            Password <span className="text-red-600">*</span>
          </label>
          <div className="overflow-hidden flex border-r-0 relative justify-center items-center">
            <input
              data-cy="password"
              type={passwordVisible ? "text" : "password"}
              name="password"
              id="password"
              placeholder="Password"
              value={password}
              onChange={handlePasswordChange}
              disabled={isLoading}
              className={`input ${!passwordIsValid && "input-error-border"}`}
            />
            {password && !isLoading && (
              <div className=" absolute right-5 flex items-center">
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className={`text-2xl text-gray-500`}
                >
                  {passwordVisible ? <IoEyeSharp /> : <FaEyeSlash />}
                </button>
              </div>
            )}
          </div>

          {!passwordIsValid && (
            <p className="input-error-msg mt-1" data-cy="required-field-error">
              {passwordError}
            </p>
          )}
        </div>

        {/* Forgot Password Link */}
        <div className="flex justify-end mt-4">
          <Link
            href="/customer/account/forgotpassword"
            className="text-neutral-400 font-400 font-calibri leading-4 font-Calibri hover:scale-105"
            data-cy="forgot-password"
            onClick={(e) => isLoading && e.preventDefault()}
          >
            Forgot Password?
          </Link>
        </div>

        {/* Login Button  */}
        <div className="mt-8">
          <button
            className="btn btn-primary p-3.5 disabled:opacity-60 disabled:cursor-not-allowed"
            data-cy="login-submit"
            disabled={false}
          >
            <div className="flex m-auto text-base leading-4">
              Login &nbsp;
              <FaArrowRightLong className="m-auto text-sm" />
            </div>
          </button>
        </div>
      </form>
    </>
  );
}
